/// <reference types="@webgpu/types" />
'use client';

import { useEffect, useState } from 'react';
import { getWebGPUFallbackReason, markWebGPUUnsupported } from '@/app/hooks/useWebGPUFallbackMode';
import { isServer } from '@/app/utils/browser';
import { reportRumWarning } from '@/app/utils/reportRum';
import logger from '@/lib/logger';

// HOW LONG THE ADAPTER REQUEST GETS BEFORE WE GIVE UP ON IT.
//
// `requestAdapter()` is specified to resolve with null when there is no
// adapter, but a driver that is wedged can leave the promise pending
// instead -- neither resolving nor rejecting. Callers use this status to
// decide WHAT TO RENDER, so a status that never leaves 'checking' is a
// page that never draws its background at all. Giving up and calling it
// unsupported costs a nicer effect; not giving up costs the whole view.
const ADAPTER_TIMEOUT_MS = 4000;
export type WebGPUSupportStatus = 'checking' | 'supported' | 'unsupported';
export const useIsWebGPUSupported = (): WebGPUSupportStatus => {
  const [status, setStatus] = useState<WebGPUSupportStatus>('checking');
  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    const settleUnsupported = () => {
      markWebGPUUnsupported();
      if (!cancelled) setStatus('unsupported');
    };
    const checkSupport = async () => {
      if (isServer) {
        return;
      }
      if (getWebGPUFallbackReason() === 'unsupported') {
        if (!cancelled) setStatus('unsupported');
        return;
      }
      if (!('gpu' in navigator)) {
        settleUnsupported();
        return;
      }
      timer = window.setTimeout(() => {
        if (cancelled) return;
        const context = {
          timeoutMs: ADAPTER_TIMEOUT_MS
        };
        logger.warn('[webgpu] adapter timed out, fallback', context);
        reportRumWarning('webgpu_adapter_timeout', context);
        settleUnsupported();
      }, ADAPTER_TIMEOUT_MS);
      try {
        const adapter = await navigator.gpu.requestAdapter();
        if (adapter) {
          if (!cancelled) setStatus('supported');
        } else {
          settleUnsupported();
        }
      } catch (e) {
        const context_0 = {
          message: e instanceof Error ? e.message : String(e)
        };
        logger.warn('[webgpu] adapter failed, fallback', context_0);
        reportRumWarning('webgpu_adapter_failed', context_0);
        settleUnsupported();
      } finally {
        window.clearTimeout(timer);
      }
    };
    checkSupport();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);
  return status;
};